import { POI_LAYOUTS, type Anchor, type PoiType } from "./pois.ts";

/**
 * Montagens dos pontos de interesse (F43, doc 08): o visual de cada POI montado com peças avulsas
 * (Dungeon pack, Models-2 e ResourceBits, processadas por `tools/assets`; sem modelo, a malha
 * procedural de reserva) no espaço local do POI (metros, +z para a frente antes do giro). Só o
 * cliente lê: colisores, pegadas, servidor e hashes continuam vindo de `POI_LAYOUTS` (o colisor da laje
 * da Caverna segue `CAVE_SLAB`, a mesma laje nas duas montagens).
 * Cada POI usa uma das montagens do tipo, escolhida pelo id.
 */
export interface PoiPieceInfo {
  /** Medidas na escala 1 (m): largura (x), altura e profundidade (z), base em y = 0 e centro em xz = 0. */
  readonly size: readonly [number, number, number];
  /** A peça cabe no círculo de raio max(x, z)/2 (pedras, barris, barracas); senão, no retângulo. */
  readonly round?: boolean;
  /** Parece sólida (parede, pilar, rocha, barril): pode cobrir um colisor e não pode ficar no caminho. */
  readonly solid: boolean;
}

/**
 * Peças das montagens. As que têm modelo no pipeline são ajustadas à altura daqui (ou ao diâmetro no
 * chão indicado no manifesto); largura e profundidade são as medidas do modelo pronto.
 */
export const POI_PIECES = {
  // Dungeon pack (pedra da fortaleza): a parede original (4 m) a 0,65, da altura do colisor da ruína.
  parede: { size: [2.6, 2.6, 0.65], solid: true },
  "parede-meia": { size: [1.3, 2.6, 0.65], solid: true },
  pilar: { size: [1.5, 4, 1.5], round: true, solid: true },
  /** Pilar deitado entre pedaços de pedra (onde ficava a moenda). */
  "pilar-caido": { size: [2.73, 1, 2.42], round: true, solid: true },
  // ResourceBits e Dungeon pack: entulho e adornos.
  entulho: { size: [0.96, 0.5, 0.68], round: true, solid: true },
  tijolos: { size: [0.75, 0.45, 0.64], solid: true },
  laje: { size: [1.33, 0.1, 1.33], solid: false },
  barril: { size: [0.89, 0.9, 0.89], round: true, solid: true },
  "barril-deitado": { size: [0.61, 0.6, 0.6], round: true, solid: true },
  caixote: { size: [0.8, 0.8, 0.8], solid: true },
  barrica: { size: [0.88, 1, 0.97], round: true, solid: true },
  banco: { size: [0.75, 0.5, 0.75], solid: true },
  mesa: { size: [1.41, 0.6, 1.5], solid: false },
  /** Tocha apagada fincada no chão (sem luz nem brilho). */
  tocha: { size: [0.29, 1.6, 0.3], round: true, solid: false },
  tabuas: { size: [1.6, 0.12, 1.33], solid: false },
  lenha: { size: [0.96, 0.6, 1.17], solid: true },
  // Models-2 (rocha natural da Caverna, doc 08: pilares de rocha).
  "pilar-rocha": { size: [2.76, 4.6, 2.99], round: true, solid: true },
  penhasco: { size: [4.59, 4.2, 5.13], round: true, solid: true },
  /** Laje de pedra apoiada nos pilares: o teto da boca da Caverna. */
  "laje-rocha": { size: [8, 1.4, 6.89], solid: true },
  pedregulho: { size: [1.75, 1.2, 1.46], round: true, solid: true },
  "pedra-solta": { size: [0.82, 0.45, 0.69], round: true, solid: true },
  // Só procedurais (sem nada parecido nos packs).
  fogueira: { size: [1.6, 0.85, 1.6], round: true, solid: true },
  "fogueira-apagada": { size: [1.3, 0.2, 1.3], round: true, solid: false },
  /** Barraca de base quadrada (2,4 m de lado, os cantos a 1,7 m do centro). */
  barraca: { size: [2.4, 2.4, 2.4], solid: true },
  /** Carroça de 2,2 × 1,3 m com rodas e o varal apoiado no chão (cabe num círculo de 1,7 m). */
  carroca: { size: [3.4, 1.5, 1.6], round: true, solid: true },
  varal: { size: [3.1, 1.8, 0.12], solid: false },
  "tronco-banco": { size: [1.6, 0.62, 0.64], solid: true },
  "destrocos-casco": { size: [3.2, 1.15, 4], solid: true },
  "destrocos-mastro": { size: [4, 0.35, 2], solid: true },
  "santuario-pedra": { size: [1.2, 2.6, 1.05], round: true, solid: true },
  /** Garganta escura da Caverna entre os pilares, sob a laje (o túnel que entra na rocha). */
  "boca-sombra": { size: [5.6, 4.4, 0.75], solid: false },
} as const satisfies Record<string, PoiPieceInfo>;

export type PoiPiece = keyof typeof POI_PIECES;

export type PoiLookPiece = Anchor & {
  piece: PoiPiece;
  /** Giro próprio (rad), somado ao do POI. */
  rot: number;
  /** Altura da base (m): negativa enterra (muro baixo de ruína), positiva apoia sobre outras peças. */
  y?: number;
  scale?: number;
  /** Entra na silhueta de longe; o padrão são as peças sólidas com 1 m ou mais de altura. */
  far?: boolean;
};

/** Uma montagem: as peças de um POI, no espaço dele. */
export type PoiLook = readonly PoiLookPiece[];

const TAU = 6.283185307179586;
/** Ângulo das 3 barracas do acampamento (`POI_LAYOUTS.acampamento`: a partir de 0,4 rad, raio 9 m). */
const tent = (i: number) => 0.4 + (i * TAU) / 3;
/** Parede da ruína com 2,5 m de largura (0,96 da peça). */
const WALL = 2.5 / 2.6;

/** As 3 barracas de frente para a fogueira (cobrem os colisores do layout). */
const TENTS: PoiLook = [0, 1, 2].map((i): PoiLookPiece => ({ piece: "barraca", a: tent(i), r: 9, rot: tent(i) + Math.PI }));

/**
 * Ruína da Fortaleza: o muro longo (10 m) em 4 paredes de alturas desiguais, os muros baixos (5 m) com
 * 2 paredes enterradas até a altura do colisor, a torre de pedra onde ficava a chaminé e, sobre o colisor da antiga moenda
 * (doc 08: a moenda sai), um pilar caído no entulho. Lajes, tijolos e entulho são só adorno.
 */
const RUIN: readonly PoiLook[] = [
  [
    { piece: "parede", x: -3.75, z: -4, rot: 0, scale: WALL },
    { piece: "parede", x: -1.25, z: -4, rot: Math.PI, y: -0.2, scale: WALL },
    { piece: "parede", x: 1.25, z: -4, rot: 0, scale: WALL },
    { piece: "parede", x: 3.75, z: -4, rot: Math.PI, y: -0.15, scale: WALL },
    { piece: "pilar", x: -4.9, z: -4, rot: 0, scale: 0.65 },
    { piece: "pilar", x: 4.5, z: -5.5, rot: 0.4, scale: 1.75 },
    { piece: "parede", x: -6, z: -2.25, rot: Math.PI / 2, y: -0.7, scale: WALL },
    { piece: "parede", x: -6, z: 0.25, rot: -Math.PI / 2, y: -0.7, scale: WALL },
    { piece: "parede", x: 6, z: 0.25, rot: Math.PI / 2, y: -1.1, scale: WALL },
    { piece: "parede", x: 6, z: 2.75, rot: -Math.PI / 2, y: -1.1, scale: WALL },
    { piece: "parede", x: -4.25, z: 4, rot: 0, y: -1.4, scale: WALL },
    { piece: "parede", x: -1.75, z: 4, rot: Math.PI, y: -1.4, scale: WALL },
    { piece: "pilar-caido", x: 0, z: 0.5, rot: 0.4 },
    { piece: "entulho", x: 0.8, z: -2.9, rot: 0.3 },
    { piece: "entulho", x: -7.6, z: 2.8, rot: 1.2 },
    { piece: "tijolos", x: 2.2, z: -7.2, rot: 0.5 },
    { piece: "tijolos", x: -2.6, z: 6.2, rot: 2.4 },
    { piece: "laje", x: 1.6, z: -1.4, rot: 0.15 },
    { piece: "laje", x: -1.4, z: -0.9, rot: 0.9 },
  ],
  [
    { piece: "parede", x: -3.75, z: -4, rot: Math.PI, y: -0.2, scale: WALL },
    { piece: "parede", x: -1.25, z: -4, rot: 0, scale: WALL },
    { piece: "pilar", x: 0, z: -4, rot: 0, scale: 0.7 },
    { piece: "parede", x: 1.25, z: -4, rot: Math.PI, scale: WALL },
    { piece: "parede", x: 3.75, z: -4, rot: 0, y: -0.15, scale: WALL },
    { piece: "pilar", x: 4.5, z: -5.5, rot: 1.2, scale: 1.6 },
    { piece: "parede-meia", x: -6, z: -2.875, rot: Math.PI / 2, y: -0.7, scale: WALL },
    { piece: "parede", x: -6, z: -1, rot: -Math.PI / 2, y: -0.85, scale: WALL },
    { piece: "parede-meia", x: -6, z: 0.875, rot: Math.PI / 2, y: -1, scale: WALL },
    { piece: "parede", x: 6, z: 0.25, rot: -Math.PI / 2, y: -1.2, scale: WALL },
    { piece: "parede", x: 6, z: 2.75, rot: Math.PI / 2, y: -1.3, scale: WALL },
    { piece: "parede", x: -4.25, z: 4, rot: Math.PI, y: -1.5, scale: WALL },
    { piece: "parede", x: -1.75, z: 4, rot: 0, y: -1.65, scale: WALL },
    { piece: "pilar-caido", x: 0, z: 0.5, rot: 2 },
    { piece: "entulho", x: 1.6, z: -0.9, rot: 1.9 },
    { piece: "entulho", x: 2.4, z: -6.8, rot: 1 },
    { piece: "tijolos", x: -7.4, z: -3.9, rot: 0.3 },
    { piece: "tijolos", x: 7.8, z: 2, rot: 1.1 },
    { piece: "laje", x: -1, z: -1.8, rot: 0.6 },
  ],
];

/**
 * Laje do teto da Caverna, a mesma nas duas montagens: o colisor dela (`POI_LAYOUTS.gruta`, caixas
 * suspensas) segue o contorno do modelo, que tem a borda recortada.
 */
export const CAVE_SLAB: PoiLookPiece = { piece: "laje-rocha", x: 0, z: -2.6, rot: 0, y: 4 };

/**
 * Caverna de rocha natural (doc 08): dois pilares de rocha empilhada, o penhasco do fundo, a laje do
 * teto apoiada neles, a garganta da entrada e os pedregulhos dos colisores; pedras soltas no pátio. O
 * pilar da esquerda é mais fino: o veio de ferro ao lado dele fica à vista. O penhasco (a 0,97, o topo
 * ainda sob a laje) fica girado com a frente atrás da garganta e as bossas viradas para os pilares:
 * de onde o jogador para, em volta, o olho fica fora dele.
 */
const CAVE: readonly PoiLook[] = [
  [
    { piece: "pilar-rocha", x: -2.6, z: -2, rot: 0, scale: 0.78 },
    { piece: "pilar-rocha", x: 2.6, z: -2, rot: 2.3, scale: 0.95 },
    { piece: "penhasco", x: 0, z: -4.2, rot: 1.4, scale: 0.97 },
    CAVE_SLAB,
    { piece: "boca-sombra", x: 0, z: -1.585, rot: 0 },
    { piece: "pedregulho", x: -5, z: 1.5, rot: 0.3 },
    { piece: "pedregulho", x: 4.8, z: 2.2, rot: 1.9 },
    { piece: "pedra-solta", x: 0.2, z: 3.6, rot: 0.7 },
    { piece: "pedra-solta", x: -5.4, z: -3.3, rot: 2.2 },
  ],
  [
    { piece: "pilar-rocha", x: -2.6, z: -2, rot: 1.7, scale: 0.78 },
    { piece: "pilar-rocha", x: 2.6, z: -2, rot: 4, scale: 0.95 },
    { piece: "penhasco", x: 0, z: -4.4, rot: 3, scale: 0.97 },
    CAVE_SLAB,
    { piece: "boca-sombra", x: 0, z: -1.585, rot: 0 },
    { piece: "pedregulho", x: -5, z: 1.5, rot: 2.1, scale: 1.05 },
    { piece: "pedregulho", x: 4.8, z: 2.2, rot: 0.2 },
    { piece: "pedra-solta", x: -0.3, z: 3.5, rot: 1.4 },
    { piece: "pedra-solta", x: 5.9, z: -2.6, rot: 0.4 },
  ],
];

/** Carroça do acampamento (`POI_LAYOUTS.acampamento`) e um ponto no espaço dela (x ao longo da caçamba). */
const CART = { x: -6.5, z: 5.5, rot: 0.6 } as const;
const onCart = (x: number, z: number) => ({
  x: CART.x + x * Math.cos(CART.rot) + z * Math.sin(CART.rot),
  z: CART.z - x * Math.sin(CART.rot) + z * Math.cos(CART.rot),
});
/** Altura da caçamba da carroça procedural. */
const CART_BED = 1.15;

/** Fogueira, barracas, carroça, varal e banco do layout, em comum às duas montagens. */
const CAMP_BASE: PoiLook = [
  { piece: "fogueira", x: 0, z: 0, rot: 0 },
  ...TENTS,
  { piece: "carroca", ...CART },
  { piece: "varal", x: 6.5, z: -6, rot: -0.4 },
  { piece: "tronco-banco", x: 2.2, z: -1.4, rot: 1.1 },
];

/**
 * Acampamento do Mercador (doc 08: tendas, carroça, barris e caixotes): barris, barricas e caixotes
 * altos só encostados nas barracas e na carroça (nos colisores delas), caixotes na caçamba; tocha
 * apagada, lenha, bancos, mesa e caixotes baixos soltos, longe dos baús e do mercador.
 */
const CAMP: readonly PoiLook[] = [
  [
    ...CAMP_BASE,
    { piece: "barril", a: tent(0) + 0.16, r: 9.35, rot: 0.3 },
    { piece: "barril", a: tent(0) - 0.15, r: 9.5, rot: 1.4, scale: 0.65 },
    { piece: "caixote", ...onCart(-0.45, 0), rot: CART.rot, y: CART_BED, scale: 0.55 },
    { piece: "caixote", ...onCart(0.35, 0.05), rot: CART.rot + 0.3, y: CART_BED, scale: 0.55 },
    { piece: "barrica", ...onCart(0.3, -1.15), rot: CART.rot + Math.PI / 2 },
    { piece: "barril", ...onCart(-0.4, 1.15), rot: 0.8 },
    { piece: "tocha", a: tent(1) - 0.2, r: 8, rot: 0 },
    { piece: "lenha", x: -1.9, z: 1.3, rot: 0.4 },
    { piece: "banco", x: -1.2, z: -1.9, rot: 0.5 },
    { piece: "banco", x: 0.2, z: 2.3, rot: 1.2 },
    { piece: "mesa", x: -5, z: -3.6, rot: 0.8 },
    { piece: "caixote", x: 7.6, z: 2.4, rot: 0.3, scale: 0.7 },
  ],
  [
    ...CAMP_BASE,
    { piece: "barril", a: tent(1) + 0.16, r: 9.35, rot: 2.2 },
    { piece: "barril", a: tent(1) - 0.15, r: 9.25, rot: 0.5 },
    { piece: "barrica", a: tent(0) - 0.15, r: 9.2, rot: tent(0) },
    { piece: "caixote", ...onCart(0.5, 1.2), rot: CART.rot, scale: 0.7 },
    { piece: "caixote", ...onCart(-0.2, 0), rot: CART.rot - 0.2, y: CART_BED, scale: 0.6 },
    { piece: "lenha", a: tent(2) + 0.2, r: 7.6, rot: tent(2) },
    { piece: "lenha", a: tent(2) - 0.25, r: 7.4, rot: tent(2) + 0.5 },
    { piece: "tocha", a: tent(2) + 0.22, r: 7.9, rot: 0 },
    { piece: "banco", x: -1.6, z: -1.2, rot: 0.2 },
    { piece: "banco", x: -0.4, z: 2.2, rot: 2.6 },
    { piece: "banco", x: 1.5, z: -2.9, rot: 1.6 },
    { piece: "tabuas", ...onCart(0.6, -1.6), rot: CART.rot + 1.4 },
  ],
];

/**
 * Naufrágio: o casco e o mastro procedurais (não há casco nos packs), com tábuas, barris e caixotes
 * espalhados na areia; os altos só encostados no casco (no colisor dele).
 */
const WRECK: readonly PoiLook[] = [
  [
    { piece: "destrocos-casco", x: 0, z: 0, rot: 0.5 },
    { piece: "destrocos-mastro", x: -3, z: 2.5, rot: 1.2 },
    { piece: "tabuas", x: 3.5, z: -2, rot: -0.7 },
    { piece: "tabuas", x: -2, z: -4, rot: 2.2 },
    { piece: "barril", x: 1.25, z: -1.3, rot: 0.4 },
    { piece: "barril-deitado", x: -1, z: 4.6, rot: 1 },
    { piece: "barril-deitado", x: 5, z: 2.8, rot: 2.6 },
    { piece: "caixote", x: -4.6, z: -1.2, rot: 0.5, scale: 0.7 },
  ],
  [
    { piece: "destrocos-casco", x: 0, z: 0, rot: -0.6 },
    { piece: "destrocos-mastro", x: -3, z: 2.5, rot: 2.4 },
    { piece: "tabuas", x: -3.8, z: -2.5, rot: 0.4 },
    { piece: "tabuas", x: 4.2, z: -3.2, rot: 1.9 },
    { piece: "tabuas", x: 1, z: 5.2, rot: -1.1 },
    { piece: "caixote", x: -1.3, z: 1, rot: 0.2 },
    { piece: "barril-deitado", x: 3.8, z: -0.6, rot: 2 },
    { piece: "barril", x: -5.2, z: 0.5, rot: 0.9, scale: 0.65 },
  ],
];

const isPiece = (id: string): id is PoiPiece => id in POI_PIECES;

/** Montagem que repete os adereços do layout (Santuário, Menir e Chegada ficam como estavam). */
function asLayout(type: PoiType): PoiLook {
  return POI_LAYOUTS[type].props.map(({ mesh, collider: _, ...anchor }) => {
    if (!isPiece(mesh)) throw new Error(`${type}: adereço sem peça de montagem (${mesh})`);
    return { ...anchor, piece: mesh };
  });
}

/** Montagens por tipo de POI (2 nos tipos do F43); tipos sem adereços não têm. */
export const POI_LOOKS: Readonly<Partial<Record<PoiType, readonly PoiLook[]>>> = {
  ruina: RUIN,
  gruta: CAVE,
  acampamento: CAMP,
  destrocos: WRECK,
  guardiao: [asLayout("guardiao")],
  pedra: [asLayout("pedra")],
  chegada: [asLayout("chegada")],
};

/** Altura do topo da peça na montagem (m). */
export const pieceTop = (p: PoiLookPiece): number => (p.y ?? 0) + POI_PIECES[p.piece].size[1] * (p.scale ?? 1);

/** A peça entra na silhueta de longe: a escolha da montagem ou, sem ela, sólida e com 1 m ou mais. */
export const inSilhouette = (p: PoiLookPiece): boolean => p.far ?? (POI_PIECES[p.piece].solid && pieceTop(p) >= 1);
